import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  DialogActions,
  Autocomplete,
  Typography,
} from "@mui/material";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  capitalize,
  getAmountWithTxnFee,
  getAmountWithoutTxnFee,
  getCatalogOptionsByFieldName,
  isCostPlusFeesModel,
  isNumeric,
} from "../../common/util";
import PercentIcon from "@mui/icons-material/Percent";
import CloseIcon from "@mui/icons-material/Close";
import Price from "../../common/component/Price";
import RadioCardGroup from "../../common/component/RadioCardGroup";
import { UserContext } from "../../context/UserContext";

export default function ProductFieldOption(props) {
  const { mode, value, category, open: controlledOpen, section = "all", notice } = props;
  const { user } = useContext(UserContext);
  const showDetails = section !== "pricing";
  const showPricing = section !== "details";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [pricingType, setPricingType] = useState("free");
  const [amountType, setAmountType] = useState("fixed");
  const [amount, setAmount] = useState("");
  const [includeFees, setIncludeFees] = useState(
    user.config?.transferFees === "false" ? false : true
  );

  const [errors, setErrors] = useState([]);
  const [amountBadInput, setAmountBadInput] = useState(false);

  const [open, setOpen] = useState(false);
  const isOpen = controlledOpen ?? open;

  const clearAmount = () => {
    setAmount("");
    setAmountBadInput(false);
  };

  const loadValue = () => {
    const feesIncluded = user.config?.transferFees === "false" ? false : true;
    setName(value?.name || "");
    setDescription(value?.description || "");
    setPricingType("free");
    setAmount("");
    setAmountType("fixed");
    setIncludeFees(feesIncluded);
    setErrors([]);
    setAmountBadInput(false);

    if (value?.pricing) {
      setPricingType("upcharge");
      if (value.pricing.factor != null && value.pricing.factor !== "") {
        setAmountType("variable");
        setAmount((100 * value.pricing.factor).toFixed(2));
      } else if (value.pricing.amount != null && value.pricing.amount !== "") {
        setAmountType("fixed");
        const stored = value.pricing.amount;
        setAmount(
          isCostPlusFeesModel(user.config?.fees) && feesIncluded
            ? getAmountWithoutTxnFee(stored, user.config?.fees)
            : stored
        );
      }
    }
  };

  useEffect(() => {
    if (controlledOpen) loadValue();
    // Reload only when a controlled editor is opened or switches options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledOpen, value]);

  const validate = () => {
    let arr = [];

    if (showPricing && pricingType === "upcharge") {
      if (amountBadInput || !String(amount || "").trim() || !isNumeric(+amount)) {
        arr.push("Enter a valid number");
      }
    } else if (amount) {
      if (!isNumeric(+amount)) {
        arr.push("Enter a valid number");
      }
    }
    return arr;
  };

  const submit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    let arr = validate();
    setErrors(arr);
    if (arr.length > 0) {
      return;
    } else {
      let o = { ...(typeof value === "object" && value ? value : {}), name, description };
      if (!showPricing) {
        // Editing name / description only: leave existing pricing untouched.
      } else if (pricingType === "upcharge") {
        if (amountType === "variable") {
          // 4 decimals so fractional percentages (e.g. 14.75%) survive the round-trip.
          const factor = (amount / 100)
            .toFixed(4)
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
          o.pricing = { type: "surcharge", factor };
        } else {
          const storedAmount =
            isCostPlusFeesModel(user.config?.fees) && includeFees
              ? getAmountWithTxnFee(amount, user.config?.fees)
              : amount;
          o.pricing = { type: "surcharge", amount: storedAmount };
        }
      } else {
        delete o.pricing;
      }
      props.onChange(o);
      handleClose();
    }
  };

  const handleDelete = () => {
    props.onChange("");
    setOpen(false);
    handleClose();
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setPricingType("free");
    setAmount("");
    setAmountType("fixed");
    setIncludeFees(user.config?.transferFees === "false" ? false : true);
    setErrors([]);
    setAmountBadInput(false);
    setOpen(false);
    props.onClose?.();
  };

  const FIELD_OPTIONS = getCatalogOptionsByFieldName(category.toLowerCase());

  return (
    <>
      <span
        onClick={() => {
          loadValue();
          if (controlledOpen === undefined) setOpen(true);
        }}
      >
        {props.children}
      </span>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <form onSubmit={submit}>
          <DialogTitle sx={{ pr: 6 }}>
            {props.title || `${capitalize(mode)} ${category} Option`}
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              {notice && <Alert severity="warning">{notice}</Alert>}
              {showDetails && (
                <Autocomplete
                  value={name}
                  onChange={(e, val) => {
                    setName(val);
                  }}
                  onInputChange={(e, val) => {
                    setName(val);
                  }}
                  options={FIELD_OPTIONS}
                  filterOptions={(options, { inputValue }) => {
                    const normalizedInput = inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        typeof option === "string" &&
                        option.toLowerCase().startsWith(normalizedInput)
                    );
                  }}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      variant="outlined"
                      {...params}
                      label="Name"
                      required={true}
                      helperText=""
                    />
                  )}
                  freeSolo
                  blurOnSelect={true}
                />
              )}
              {showDetails && (
                <TextField
                  name="description"
                  value={description}
                  variant="outlined"
                  fullWidth
                  id="description"
                  label="Description (optional)"
                  inputProps={{ maxLength: 100 }}
                  onChange={(e) => setDescription(e.target.value)}
                  helperText="A short description to help customers compare options"
                />
              )}
              {showPricing && (
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Pricing
                  </Typography>
                  <RadioCardGroup
                    name="pricing"
                    direction="row"
                    value={pricingType}
                    onChange={(e) => {
                      const next = e.target.value;
                      setPricingType(next);
                      if (next !== "upcharge") {
                        setAmountType("fixed");
                        clearAmount();
                      }
                    }}
                    options={[
                      { value: "free", title: "Free" },
                      { value: "upcharge", title: "Upcharge" },
                    ]}
                  />
                </Box>
              )}
              {showPricing && pricingType === "upcharge" && (
                <Box>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Upcharge Type
                  </Typography>
                  <RadioCardGroup
                    name="amountType"
                    direction="row"
                    value={amountType}
                    onChange={(e) => {
                      setAmountType(e.target.value);
                      clearAmount();
                    }}
                    options={[
                      { value: "fixed", title: "Fixed" },
                      { value: "variable", title: "Percentage" },
                    ]}
                  />
                </Box>
              )}
              {showPricing && pricingType === "upcharge" && (
                <Box sx={{ mt: 1 }}>
                  {amountType === "fixed" && isCostPlusFeesModel(user.config?.fees) ? (
                    <Price
                      label="Upcharge Amount"
                      required
                      value={{ amount, includeFees }}
                      onChange={(o) => {
                        setAmount(o.amount);
                        setIncludeFees(o.includeFees);
                        setAmountBadInput(Boolean(o.badInput));
                        setErrors([]);
                      }}
                    />
                  ) : (
                    <TextField
                      name="amount"
                      value={amount}
                      variant="outlined"
                      id="amount"
                      label="Upcharge Amount"
                      onChange={(e) => {
                        setAmount(e.target.value);
                        setAmountBadInput(false);
                        setErrors([]);
                      }}
                      onWheel={(e) => {
                        e.target && e.target.blur();
                      }}
                      type="text"
                      onInvalid={(e) => e.target.setCustomValidity("Enter a valid amount")}
                      onInput={(e) => e.target.setCustomValidity("")}
                      inputProps={{
                        pattern: "^[0-9]*(.[0-9]{1,2})?$",
                        inputmode: "decimal",
                      }}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment:
                          amountType === "fixed" ? (
                            <InputAdornment position="start">
                              <AttachMoneyOutlinedIcon />
                            </InputAdornment>
                          ) : (
                            ""
                          ),
                        endAdornment:
                          amountType === "variable" ? (
                            <InputAdornment position="end">
                              <PercentIcon />
                            </InputAdornment>
                          ) : (
                            ""
                          ),
                      }}
                    />
                  )}
                </Box>
              )}
              {errors.length > 0 && (
                <Alert sx={{ justifyContent: "center" }} severity="error">
                  {errors.join(". ")}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {value && props.showDelete !== false && (
              <Button fullWidth onClick={handleDelete} variant="outlined" color="error">
                Delete
              </Button>
            )}

            <Button fullWidth variant="contained" color="primary" type="submit">
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
